import React, { useContext, useState } from "react";
import { Link, useNavigate } from "@reach/router";
import { RfxContext } from "newui/features/Rfx/context/RfxContext";
import { CustomModal } from "components/RFQ/Modal/CustomModal";
import { Button } from "newui/components/Button";
import { useHasNoRbacAccess } from "hooks/auth";

interface AppMenuLinkProps {
  /**
   * PRO-737: keep this item clickable for a user who holds no RBAC standing.
   * Only the surfaces such a user may actually reach set this — My Profile —
   * so the default is that a roleless user cannot navigate anywhere.
   */
  alwaysEnabled?: boolean;
  children: React.ReactNode;
  className?: string;
  exactLink?: boolean;
  newTab?: boolean;
  testId?: string;
  to: string;
  onNavigate?: () => void;
}

export const AppMenuLink: React.FC<AppMenuLinkProps> = ({
  to,
  newTab = false,
  exactLink = false,
  testId,
  className,
  children,
  onNavigate,
  alwaysEnabled = false,
}) => {
  const { setUploadInProgresses, uploadInProgresses } = useContext(RfxContext);
  const hasNoRbacAccess = useHasNoRbacAccess();
  const isDisabled = hasNoRbacAccess && !alwaysEnabled;
  const [isWarningModalOpen, setWarningModalOpen] = useState(false);
  const navigate = useNavigate();

  const isActive = ({ isPartiallyCurrent }: { isPartiallyCurrent: boolean }) => {
    const url = new URL(window.location.href);
    const getLastLink = (urls: string) => urls?.split("/").pop();
    const isExact = getLastLink(url.pathname) === getLastLink(to);

    if (exactLink ? isExact : isPartiallyCurrent) {
      if (isExact) {
        return {
          style: {
            backgroundColor: "rgba(0, 69, 245, 0.2)",
            color: "#0045F5",
          },
        };
      }
    }

    return {};
  };

  const handleLinkClick = (e: React.MouseEvent) => {
    if (
      uploadInProgresses?.rfxBidder?.length > 0 ||
      uploadInProgresses?.rfxProcurer?.length > 0
    ) {
      e.preventDefault();
      setWarningModalOpen(true);
    } else if (onNavigate) {
      onNavigate();
    }
  };

  const FooterComponent = () => {
    return (
      <div className="flex w-full justify-between gap-2 text-utility-error-50">
        <Button
          onClick={() => {
            setWarningModalOpen(false);
          }}
          btntype="base"
          className="!w-full"
        >
          Cancel
        </Button>
        <Button
          className="flex !w-full gap-3"
          onClick={() => {
            setWarningModalOpen(false);
            if (uploadInProgresses?.rfxBidder?.length) {
              setUploadInProgresses({ ...uploadInProgresses, rfxBidder: [] });
            } else {
              setUploadInProgresses({ ...uploadInProgresses, rfxProcurer: [] });
            }
            navigate(to);
            if (onNavigate) {
              onNavigate();
            }
          }}
        >
          Yes
        </Button>
      </div>
    );
  };

  const linkClass = `flex w-full items-center text-gray-500 font-medium ${className ?? ""}`;

  /*
   * PRO-737 outcome 2: every menu item is inert for a user with no RBAC
   * standing. Done here rather than at each call site because AppMenuLink is
   * the single element every menu entry renders through, so a menu item added
   * later is disabled by default instead of silently escaping the lockout.
   *
   * Rendered as a non-interactive span, not a styled-but-live link: pointer
   * events alone still leave the target reachable by keyboard and by the
   * browser's own link handling.
   */
  if (isDisabled) {
    return (
      <span
        className={`${linkClass} cursor-not-allowed opacity-50`}
        aria-disabled="true"
        data-testid={testId}
      >
        {children}
      </span>
    );
  }

  return (
    <>
      {newTab ? (
        <a
          href={to}
          target="_blank"
          rel="noopener noreferrer"
          className={linkClass}
          data-testid={testId}
        >
          {children}
        </a>
      ) : (
        <Link
          getProps={isActive}
          to={to}
          className={linkClass}
          onClick={handleLinkClick}
          data-testid={testId}
        >
          {children}
        </Link>
      )}
      <CustomModal
        isOpen={isWarningModalOpen}
        onClose={() => setWarningModalOpen(false)}
        header={{
          icon: "warning",
          bgColor: "bg-utility-error-50",
          iconColor: "text-utility-error-500",
          title: "Are your sure to leave current page?",
          description:
            "Are you sure you want to leave this page? Some of your changes haven't been saved.",
        }}
        footerComponent={<FooterComponent />}
      >
        {null}
      </CustomModal>
    </>
  );
};
